import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import {
	formatDate,
	formatNumber,
	parseTags,
} from '@sohanscript/lib/utils/format';
import { Heading } from '@sohanscript/ui/heading';
import { List, ListItem } from '@sohanscript/ui/list';
import { Motion } from '@sohanscript/ui/motion';
import { type ComponentProps, useMemo } from 'react';
import { api } from '#/lib/trpc/client.ts';
import { Img } from '@/components/image';
import { Link } from '@/components/link';
import type { RouterOutputs } from '@/server/root';
import { Button } from '../ui/button';

type Note = RouterOutputs['note']['list'][number];

const MAX_READS = 3;
const WORDS_PER_MINUTE = 200;
const MAX_MATCHED_TAGS = 2;
const EXCERPT_CHARS = 140;

type Props = ComponentProps<'section'> & {
	tags?: string[];
	currentSlug?: string;
};

function useSimilarNotes(tags?: string[], currentSlug?: string) {
	const [notes] = api.note.list.useSuspenseQuery({
		page: 1,
		perPage: 99999,
	});

	if (!notes?.length) return [];

	const others = notes.filter(
		(a) => a.slug?.current && a.slug.current !== currentSlug,
	);

	if (!tags?.length) return others.slice(0, MAX_READS);

	const scored = others.map((a) => {
		const noteTags = parseTags(a.tags);
		const noteTagsSet = new Set(noteTags);
		const score = tags.filter((t) => noteTagsSet.has(t)).length;
		return { note: a, score };
	});

	return scored
		.filter(({ score }) => score > 0)
		.sort((a, b) => b.score - a.score)
		.map(({ note }) => note)
		.slice(0, MAX_READS)
		.concat(scored.filter(({ score }) => score === 0).map(({ note }) => note))
		.slice(0, MAX_READS);
}

function SimilarReadRow({
	note,
	tags,
	index,
}: {
	note: Note;
	tags?: string[];
	index: number;
}) {
	const slug = note.slug?.current ?? '';
	const date = note.publishedAt
		? formatDate(note.publishedAt, { month: 'short' })
		: null;
	const readingTime = note.body
		? Math.ceil(note.body.trim().split(/\s+/).length / WORDS_PER_MINUTE)
		: null;
	const excerpt = note.description
		? note.description
		: note.body
			? `${note.body.slice(0, EXCERPT_CHARS).replace(/\n/g, ' ').trim()}…`
			: null;
	const matched = useMemo(
		() => {
			if (!tags?.length) return [];
			const tagSet = new Set(tags);
			return parseTags(note.tags)
				.filter((tag) => tagSet.has(tag))
				.slice(0, MAX_MATCHED_TAGS);
		},
		[note.tags, tags],
	);

	return (
		<ListItem asChild>
			<Motion
				as="li"
				initial="hidden"
				delay={index * 0.08}
				className="relative flex items-center gap-4 py-5 md:gap-6 max-md:flex-col"
			>
				{note.image && (
					<Img
						src={note.image}
						alt=""
						width={160}
						sizes="(min-width: 768px) 80px, 56px"
						placeholder="shimmer"
						className="aspect-video shrink-0 max-md:w-full rounded-md corner-bevel object-cover transition duration-500 md:h-25"
					/>
				)}
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-2xs text-muted-foreground uppercase tracking-widest">
						{date && (
							<time dateTime={note.publishedAt ?? undefined}>{date}</time>
						)}
						{date && readingTime && (
							<span aria-hidden className="text-border">
								·
							</span>
						)}
						{readingTime && (
							<span className="tabular-nums">
								{m.reads_min_read({ count: formatNumber(readingTime) })}
							</span>
						)}
						{matched.map((tag) => (
							<span key={tag} className="text-primary/80">
								#{tag}
							</span>
						))}
					</div>
					<h3 className="mt-1.5 font-xblk text-foreground-light text-lg leading-snug transition-colors duration-300 group-focus-within:text-primary group-hover:text-primary md:text-xl">
						{note.title}
					</h3>
					{excerpt && (
						<p className="mt-1 max-w-[80ch] line-clamp-2 text-foreground/60 text-sm leading-relaxed">
							{excerpt}
						</p>
					)}
				</div>
				<span
					aria-hidden
					className="hidden shrink-0 items-center gap-3 font-mono text-2xs text-muted-foreground uppercase tracking-widest lg:flex"
				>
					<span className="max-w-40 truncate text-foreground/40">
						~/note/{slug}
					</span>
					<Iconify
						icon="lucide:arrow-right"
						className="size-4 text-primary transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1"
					/>
				</span>
				<Link
					to="/note/$slug"
					params={{ slug } as never}
					viewTransition={{ types: ['slide-left'] }}
					aria-label={m.note_read_card_aria({ title: note.title ?? '' })}
					className="absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				/>
			</Motion>
		</ListItem>
	);
}

export function SimilarReads({
	tags,
	currentSlug,
	className,
	...props
}: Props) {
	const display = useSimilarNotes(tags, currentSlug);

	if (!display.length) return null;

	return (
		<section
			aria-labelledby="sr-heading"
			id="similar-reads"
			className={cn('container pt-40', className)}
			{...props}
		>
			<Heading
				variant="start"
				titlePrefix={m.heading_reads_prefix()}
				titleHighlight={m.heading_reads_highlight()}
				description={m.reads_heading_tagline()}
				className="mt-4 max-w-2xl"
			/>

			<List as="ol" className="mt-10 flex flex-col">
				{display.map((note, index) => (
					<SimilarReadRow
						key={note.slug?.current}
						note={note}
						tags={tags}
						index={index}
					/>
				))}
			</List>
			<Button
				href="/notes"
				className="group mt-10 flex w-fit"
				variant="opacity"
			>
				{m.reads_all_notes()}
				<Iconify
					icon="lucide:arrow-right"
					className="size-3.5 transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5"
				/>
			</Button>
		</section>
	);
}
